<script>
import { ICONS } from '@modules/atendimento/constants/Text'
import { stringToColor } from '@modules/global/utils/Color'
import ChatActionsMenu from '@modules/atendimento/components/chat-actions-menu/chat-actions-menu.vue'

export default {
  name: 'ChatWindowHeader',
  components: { ChatActionsMenu },
  props: {
    selectedConversa: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    // Conexões WhatsApp do escritório (vivem em data() do host, não no Vuex).
    // Usadas para resolver e exibir o chip do nome da conexão da conversa
    // aberta quando há 2+ conexões (gestor que enxerga todas).
    conexoes: {
      type: Array,
      default() {
        return []
      },
    },
    showCloseConversa: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      icons: ICONS,
    }
  },
  computed: {
    contato() {
      return (this.selectedConversa && this.selectedConversa.contato) || {}
    },
    contactName() {
      // Grupos: só o subject (`nome`); nome_contato/nome_cliente não
      // existem pra grupo. Individuais: cliente > contact book > profile
      // pushName > telefone.
      if (this.contato.is_group) {
        return this.contato.nome || this.contato.telefone || 'Grupo'
      }
      return (
        this.contato.nome_cliente ||
        this.contato.nome_contato ||
        this.contato.nome ||
        this.contato.telefone ||
        ''
      )
    },
    contactPhone() {
      return this.contato.telefone || ''
    },
    contactFotoUrl() {
      return this.contato.foto_url || ''
    },
    isGroup() {
      return !!this.contato.is_group
    },
    participantCount() {
      if (!this.isGroup) return 0
      const participantes = this.contato.participantes
      return Array.isArray(participantes) ? participantes.length : 0
    },
    // Conexão da conversa aberta, resolvida pelo id_conexao da conversa.
    // Type-safe: compara como string para cobrir o caso em que o localStorage
    // persiste como string (Number|String) e c.id vem como number do backend.
    conexaoAtual() {
      const idConexao = this.selectedConversa && this.selectedConversa.id_conexao
      if (!idConexao) return null
      const list = this.conexoes || []
      return list.find((c) => c && String(c.id) === String(idConexao)) || null
    },
    // Mostra o chip sempre que a conexão da conversa estiver resolvida. O gate
    // em `conexaoAtual` evita exibir um chip "vazio" durante o load assíncrono
    // das conexões — só aparece quando já há nome real para mostrar.
    showConexaoChip() {
      return !!this.conexaoAtual
    },
    conexaoNome() {
      if (!this.conexaoAtual) return ''
      return this.conexaoAtual.nome || 'Conexão #' + this.conexaoAtual.id
    },
    // Cor determinística por string — mesmo padrão do componente client-tags.
    conexaoChipColor() {
      return stringToColor(this.conexaoNome)
    },
  },
  methods: {
    onOpenProfile() {
      if (!this.contactFotoUrl) return
      this.$emit('open-profile', {
        imageUrl: this.contactFotoUrl,
        name: this.contactName,
      })
    },
    onAtribuir() {
      this.$emit('atribuir')
    },
    onTransferir() {
      this.$emit('transferir')
    },
    onEscalar() {
      this.$emit('escalar')
    },
    onFechar() {
      this.$emit('fechar')
    },
    onImportHistory() {
      this.$emit('import-history')
    },
    onCloseConversa() {
      this.$emit('close-conversa')
    },
  },
}
</script>

<template>
  <div class="chat-window__header d-flex align-center px-4 py-3">
    <v-avatar
      size="36"
      :color="isGroup ? 'blue-grey lighten-3' : 'grey lighten-2'"
      class="mr-3"
      :class="{ 'avatar-clickable': contactFotoUrl }"
      @click="onOpenProfile"
    >
      <img v-if="contactFotoUrl" :src="contactFotoUrl" :alt="contactName" />
      <icon-svg
        v-else-if="isGroup"
        :name="icons.accountGroup"
        size="20"
        color="white"
      />
      <span v-else class="az-text-primary az-size-14 font-medium">{{
        contactName.charAt(0)
      }}</span>
    </v-avatar>
    <div class="flex-grow-1">
      <div class="az-text-primary az-size-14 font-medium">{{
        contactName
      }}</div>
      <div v-if="isGroup" class="az-text-small">
        Grupo WhatsApp<span v-if="participantCount > 0">
          · {{ participantCount }} participantes</span
        >
      </div>
      <div v-else class="az-text-small">{{ contactPhone }}</div>
      <v-chip
        v-if="showConexaoChip"
        x-small
        class="mt-1"
        :color="$setOpacity(conexaoChipColor, 0.15)"
        :text-color="conexaoChipColor"
        data-test="conexao-chip"
      >
        <icon-svg
          :name="icons.connections"
          size="12"
          :color="conexaoChipColor"
          class="mr-1"
        />
        {{ conexaoNome }}
      </v-chip>
    </div>

    <ChatActionsMenu
      :conversa="selectedConversa"
      data-test="chat-actions-menu"
      @atribuir="onAtribuir"
      @transferir="onTransferir"
      @escalar="onEscalar"
      @fechar="onFechar"
    />

    <v-tooltip bottom>
      <template v-slot:activator="{ on }">
        <v-btn
          icon
          small
          data-test="import-history-btn"
          class="ml-1"
          v-on="on"
          @click="onImportHistory"
        >
          <icon-svg :name="icons.history" size="16" color="#8E8E8E" />
        </v-btn>
      </template>
      <span>Importar histórico do WhatsApp</span>
    </v-tooltip>

    <v-tooltip v-if="showCloseConversa" bottom>
      <template v-slot:activator="{ on }">
        <v-btn
          icon
          small
          data-test="close-conversa-btn"
          class="ml-1"
          v-on="on"
          @click="onCloseConversa"
        >
          <icon-svg :name="icons.close" size="12" color="#8E8E8E" />
        </v-btn>
      </template>
      <span>Fechar conversa</span>
    </v-tooltip>
  </div>
</template>

<style lang="sass" scoped>
.chat-window__header
  background-color: var(--panel-bg)
  border-bottom: 1px solid #e0e0e0

.avatar-clickable
  cursor: pointer
</style>
